import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Building2, Plus } from "lucide-react";
import { Button, Card, Dialog, Input, Spinner } from "@nutrayan/ui";
import {
  listTenants,
  createTenant,
  updateTenant,
  activateTenant,
  deactivateTenant,
  type AdminTenantView,
} from "@/lib/tenant-api";
import { useClaims } from "@/lib/use-claims";
import { isBootstrapLevel } from "@/lib/permissions";
import { StatusBadge } from "@/components/StatusBadge";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import UnauthorizedPage from "@/routes/UnauthorizedPage";

// Tenant directory (BLK-API-018) — bootstrap super admin only. The backend gates
// /admin/api/v1/tenants on the ADMIN_LEVEL_BOOTSTRAP_SUPER authority; this page
// mirrors that from the JWT adminLevel claim so no one sees a door they can't open.

const createSchema = z.object({
  code: z
    .string()
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "kebab-case only (a-z, 0-9, hyphens)")
    .min(2)
    .max(40),
  displayName: z.string().min(2, "At least 2 characters").max(120),
  domain: z.string().max(200).optional().or(z.literal("")),
});
type CreateValues = z.infer<typeof createSchema>;

const editSchema = z.object({
  displayName: z.string().min(2, "At least 2 characters").max(120),
  domain: z.string().max(200).optional().or(z.literal("")),
});
type EditValues = z.infer<typeof editSchema>;

function FieldError({ message }: { message?: string | undefined }) {
  return message ? <p className="text-caption text-terracotta mt-1">{message}</p> : null;
}

export default function TenantsPage() {
  const { adminLevel } = useClaims();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminTenantView | null>(null);
  const [deactivating, setDeactivating] = useState<AdminTenantView | null>(null);

  const tenants = useQuery({
    queryKey: ["admin", "tenants"],
    queryFn: listTenants,
    enabled: isBootstrapLevel(adminLevel),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "tenants"] });

  const create = useMutation({
    mutationFn: (v: CreateValues) =>
      createTenant({
        code: v.code,
        displayName: v.displayName.trim(),
        ...(v.domain ? { domain: v.domain.trim() } : {}),
      }),
    onSuccess: (t) => {
      toast.success(`Tenant "${t.displayName ?? t.id}" created`);
      invalidate();
      setCreating(false);
      createForm.reset();
    },
    onError: (e: Error) => toast.error(e.message || "Couldn't create the tenant"),
  });

  const update = useMutation({
    mutationFn: (v: EditValues & { id: string }) =>
      updateTenant(v.id, { displayName: v.displayName.trim(), domain: v.domain?.trim() ?? "" }),
    onSuccess: () => {
      toast.success("Tenant updated");
      invalidate();
      setEditing(null);
    },
    onError: (e: Error) => toast.error(e.message || "Couldn't save"),
  });

  const activate = useMutation({
    mutationFn: (id: string) => activateTenant(id),
    onSuccess: () => {
      toast.success("Tenant activated");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message || "Action failed"),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => deactivateTenant(id),
    onSuccess: () => {
      toast.success("Tenant deactivated");
      invalidate();
      setDeactivating(null);
    },
    onError: (e: Error) => toast.error(e.message || "Action failed"),
  });

  const createForm = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { code: "", displayName: "", domain: "" },
  });

  const editForm = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    values: { displayName: editing?.displayName ?? "", domain: editing?.domain ?? "" },
  });

  if (!isBootstrapLevel(adminLevel)) return <UnauthorizedPage />;

  const rows = tenants.data ?? [];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-display-xl text-charcoal">Tenants</h1>
          <p className="text-body text-ink mt-1">
            Storefront tenants on this platform. The platform tenant itself is not listed.
          </p>
        </div>
        <Button size="md" onClick={() => setCreating(true)}>
          <Plus size={16} aria-hidden />
          New tenant
        </Button>
      </header>

      {tenants.isPending ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : tenants.isError ? (
        <Card>
          <p className="text-body text-terracotta">Couldn&apos;t load tenants.</p>
        </Card>
      ) : rows.length === 0 ? (
        <Card className="flex flex-col items-center py-16 text-center">
          <span className="bg-sand text-muted flex h-14 w-14 items-center justify-center rounded-full">
            <Building2 size={26} aria-hidden />
          </span>
          <h2 className="font-display text-display-sm text-charcoal mt-4">No tenants yet</h2>
          <p className="text-body-sm text-ink mt-1 max-w-sm">
            Create the first storefront tenant to start onboarding a brand.
          </p>
          <Button size="md" className="mt-4" onClick={() => setCreating(true)}>
            New tenant
          </Button>
        </Card>
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="text-body-sm w-full min-w-[44rem]">
            <thead className="border-charcoal/10 text-caption text-muted border-b text-left">
              <tr>
                <th className="px-4 py-2.5">Code</th>
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Domain</th>
                <th className="px-4 py-2.5">Locale / Currency</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className="border-charcoal/5 hover:bg-sand/40 border-b">
                  <td className="text-ink px-4 py-2.5 font-mono">{t.id}</td>
                  <td className="text-charcoal px-4 py-2.5 font-medium">{t.displayName ?? "—"}</td>
                  <td className="text-ink px-4 py-2.5">{t.domain || "—"}</td>
                  <td className="text-ink px-4 py-2.5">
                    {t.primaryLocale ?? "—"} · {t.primaryCurrency ?? "—"}
                  </td>
                  <td className="px-4 py-2.5">
                    <StatusBadge status={t.active ? "ACTIVE" : "SUSPENDED"} />
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setEditing(t)}>
                        Edit
                      </Button>
                      {t.active ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-terracotta text-terracotta hover:bg-terracotta/5"
                          disabled={deactivate.isPending}
                          onClick={() => setDeactivating(t)}
                        >
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={activate.isPending}
                          onClick={() => activate.mutate(t.id)}
                        >
                          Activate
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Create */}
      <Dialog
        open={creating}
        onClose={() => setCreating(false)}
        title="New tenant"
        description="The code is permanent and used in URLs and API scoping."
      >
        <form
          className="space-y-4"
          onSubmit={createForm.handleSubmit((v) => create.mutate(v))}
          noValidate
        >
          <div>
            <label className="text-body-sm text-ink" htmlFor="tenant-code">
              Code
            </label>
            <Input
              id="tenant-code"
              placeholder="acme-ayurveda"
              {...createForm.register("code")}
              invalid={!!createForm.formState.errors.code}
            />
            <FieldError message={createForm.formState.errors.code?.message} />
          </div>
          <div>
            <label className="text-body-sm text-ink" htmlFor="tenant-name">
              Display name
            </label>
            <Input
              id="tenant-name"
              placeholder="Acme Ayurveda"
              {...createForm.register("displayName")}
              invalid={!!createForm.formState.errors.displayName}
            />
            <FieldError message={createForm.formState.errors.displayName?.message} />
          </div>
          <div>
            <label className="text-body-sm text-ink" htmlFor="tenant-domain">
              Domain <span className="text-muted">(optional)</span>
            </label>
            <Input
              id="tenant-domain"
              placeholder="shop.acme.in"
              {...createForm.register("domain")}
              invalid={!!createForm.formState.errors.domain}
            />
            <FieldError message={createForm.formState.errors.domain?.message} />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Creating…" : "Create tenant"}
            </Button>
          </div>
        </form>
      </Dialog>

      {/* Edit */}
      <Dialog
        open={!!editing}
        onClose={() => setEditing(null)}
        title={`Edit ${editing?.displayName ?? editing?.id ?? "tenant"}`}
        description="Only the display name and domain are editable; the code is permanent."
      >
        <form
          className="space-y-4"
          onSubmit={editForm.handleSubmit(
            (v) => editing && update.mutate({ ...v, id: editing.id }),
          )}
          noValidate
        >
          <div>
            <label className="text-body-sm text-ink" htmlFor="edit-tenant-name">
              Display name
            </label>
            <Input
              id="edit-tenant-name"
              {...editForm.register("displayName")}
              invalid={!!editForm.formState.errors.displayName}
            />
            <FieldError message={editForm.formState.errors.displayName?.message} />
          </div>
          <div>
            <label className="text-body-sm text-ink" htmlFor="edit-tenant-domain">
              Domain <span className="text-muted">(blank clears it)</span>
            </label>
            <Input id="edit-tenant-domain" {...editForm.register("domain")} />
            <FieldError message={editForm.formState.errors.domain?.message} />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!deactivating}
        title="Deactivate tenant?"
        description={`"${deactivating?.displayName ?? deactivating?.id ?? ""}" will stop serving its storefront until reactivated.`}
        confirmLabel="Deactivate"
        tone="danger"
        loading={deactivate.isPending}
        onConfirm={() => deactivating && deactivate.mutate(deactivating.id)}
        onClose={() => setDeactivating(null)}
      />
    </div>
  );
}
